import {
  generateId,
  getDatabase,
  updateDatabase
} from '../data/localDataStore';

class ProductSyncService {
  mapExternalProduct(product) {
    return {
      id: product?.id || generateId('product'),
      sku: product?.sku,
      name: product?.name || '',
      publicPrice: Number(product?.publicPrice ?? product?.price ?? 0),
      resellerPrice: Number(product?.resellerPrice ?? product?.price ?? 0),
      category: product?.category || 'other',
      is_active: product?.is_active ?? true,
      created_at: product?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  async syncExternalProductsProgressive(productsData, onProgress = null) {
    try {
      if (!Array.isArray(productsData)) {
        return {
          success: false,
          error: 'Dati prodotti non validi - deve essere un array'
        };
      }

      const batchSize = 25;
      let startIndex = 0;
      let totalCreated = 0;
      let totalExisted = 0;
      let totalErrored = 0;
      let totalProcessed = 0;

      while (startIndex < productsData.length) {
        const batch = productsData.slice(startIndex, startIndex + batchSize);
        let batchCreated = 0;
        let batchExisted = 0;

        updateDatabase((draft) => {
          batch.forEach((product) => {
            if (!product?.sku) {
              totalErrored += 1;
              return;
            }

            const existingProduct = draft.products.find((item) => item.sku === product.sku);

            if (existingProduct) {
              Object.assign(
                existingProduct,
                this.mapExternalProduct({
                  ...existingProduct,
                  ...product,
                  id: existingProduct.id,
                  created_at: existingProduct.created_at
                })
              );
              batchExisted += 1;
              return;
            }

            draft.products.push(this.mapExternalProduct(product));
            batchCreated += 1;
          });
        });

        totalCreated += batchCreated;
        totalExisted += batchExisted;
        totalProcessed += batch.length;

        if (onProgress) {
          onProgress({
            currentBatch: Math.floor(startIndex / batchSize) + 1,
            totalBatches: Math.ceil(productsData.length / batchSize),
            processed: totalProcessed,
            created: totalCreated,
            existed: totalExisted,
            errors: totalErrored,
            percentage: Math.round((totalProcessed / productsData.length) * 100)
          });
        }

        startIndex += batchSize;

        if (startIndex < productsData.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      return {
        success: true,
        data: {
          totalProcessed,
          productsCreated: totalCreated,
          productsExisted: totalExisted,
          productsErrored: totalErrored,
          batchesProcessed: Math.ceil(productsData.length / batchSize)
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Errore imprevisto nella sincronizzazione: ${error?.message}`
      };
    }
  }

  async createProductInstant(sku, name = '', publicPrice = 0, resellerPrice = 0, category = 'other') {
    try {
      const database = getDatabase();
      const existingProduct = database.products.find((product) => product.sku === sku);

      if (existingProduct) {
        return {
          success: true,
          data: {
            productId: existingProduct.id,
            wasCreated: false,
            message: 'Prodotto già presente nel database locale'
          }
        };
      }

      const newProduct = this.mapExternalProduct({
        sku,
        name,
        publicPrice,
        resellerPrice,
        category
      });

      updateDatabase((draft) => {
        draft.products.push(newProduct);
      });

      return {
        success: true,
        data: {
          productId: newProduct.id,
          wasCreated: true,
          message: 'Prodotto creato nel database locale'
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Errore imprevisto: ${error?.message}`
      };
    }
  }

  async getSyncRequirements() {
    try {
      const database = getDatabase();
      const flaggedSkus = [...new Set(database.productCartFlags.map((flag) => flag.product_sku))];
      const existingSkus = new Set(database.products.map((product) => product.sku));
      const missingSkus = flaggedSkus.filter((sku) => !existingSkus.has(sku));

      return {
        success: true,
        data: {
          totalFlaggedSkus: flaggedSkus.length,
          missingProducts: missingSkus.length,
          missingSkus,
          syncNeeded: missingSkus.length > 0,
          recommendations: missingSkus.length
            ? ['Sincronizza i prodotti del foglio Google per allineare il database locale.']
            : ['Database locale già allineato con i prodotti flaggati.']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Errore imprevisto: ${error?.message}`
      };
    }
  }

  async autoSyncOnStartup(productsData, onProgress = null) {
    try {
      const requirementsResult = await this.getSyncRequirements();

      if (requirementsResult?.success && !requirementsResult?.data?.syncNeeded) {
        return {
          success: true,
          data: {
            skipped: true,
            reason: 'Nessuna sincronizzazione necessaria - tutti i prodotti flaggati esistono già',
            totalProcessed: 0,
            productsCreated: 0,
            productsExisted: requirementsResult?.data?.totalFlaggedSkus || 0
          }
        };
      }

      if (onProgress) {
        onProgress({
          phase: 'STARTUP_SYNC',
          message: 'Sincronizzazione automatica all\'avvio...'
        });
      }

      const syncResult = await this.syncExternalProductsProgressive(productsData, (progress) => {
        if (onProgress) {
          onProgress({
            ...progress,
            phase: 'PROGRESSIVE_SYNC',
            message: `Sincronizzazione batch ${progress?.currentBatch}/${progress?.totalBatches}...`
          });
        }
      });

      if (!syncResult?.success) {
        return syncResult;
      }

      if (onProgress) {
        onProgress({
          phase: 'COMPLETED',
          message: `Sincronizzazione completata: ${syncResult?.data?.productsCreated} prodotti creati`,
          ...syncResult.data
        });
      }

      return {
        success: true,
        data: {
          ...syncResult.data,
          autoSync: true,
          phase: 'STARTUP_COMPLETE'
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Errore nell'auto-sync all'avvio: ${error?.message}`
      };
    }
  }

  async syncExternalProducts(productsData) {
    return this.syncExternalProductsProgressive(productsData);
  }

  async validateAndCreateProduct(sku, name = '', publicPrice = 0, resellerPrice = 0, category = 'other') {
    const result = await this.createProductInstant(sku, name, publicPrice, resellerPrice, category);

    return {
      ...result,
      data: result?.data
        ? {
            actionTaken: result.data.wasCreated ? 'created' : 'existing',
            productId: result.data.productId,
            skuUsed: sku,
            wasCreated: result.data.wasCreated,
            errorMessage: null
          }
        : undefined
    };
  }

  async getSyncStats() {
    try {
      const database = getDatabase();
      const flaggedSkus = [...new Set(database.productCartFlags.map((flag) => flag.product_sku))];
      const existingSkus = new Set(database.products.map((product) => product.sku));
      const flaggedProductsMissingFromDb = flaggedSkus.filter((sku) => !existingSkus.has(sku)).length;

      return {
        success: true,
        data: {
          totalProductsInDb: database.products.length,
          totalFlaggedProducts: flaggedSkus.length,
          flaggedProductsMissingFromDb,
          recentlyAutoCreated: 0,
          syncHealthStatus: flaggedProductsMissingFromDb === 0 ? 'Allineato' : 'Da sincronizzare',
          recommendations: flaggedProductsMissingFromDb === 0
            ? ['Nessuna azione richiesta.']
            : ['Esegui una sincronizzazione manuale dei prodotti.']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Errore imprevisto: ${error?.message}`
      };
    }
  }

  async checkSyncNeeded(productsData) {
    try {
      if (!Array.isArray(productsData)) {
        return { success: false, error: 'Dati prodotti non validi' };
      }

      const externalSkus = productsData.map((product) => product?.sku).filter(Boolean);
      if (externalSkus.length === 0) {
        return {
          success: true,
          data: {
            syncNeeded: false,
            reason: 'Nessun prodotto valido nei dati esterni'
          }
        };
      }

      const database = getDatabase();
      const existingSKUs = database.products
        .filter((product) => externalSkus.includes(product.sku))
        .map((product) => product.sku);
      const missingSKUs = externalSkus.filter((sku) => !existingSKUs.includes(sku));

      return {
        success: true,
        data: {
          syncNeeded: missingSKUs.length > 0,
          totalExternal: externalSkus.length,
          totalExisting: existingSKUs.length,
          totalMissing: missingSKUs.length,
          missingSKUs: missingSKUs.slice(0, 10),
          syncRecommended: missingSKUs.length > 0
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Errore nel controllo sincronizzazione: ${error?.message}`
      };
    }
  }

  calculateSyncScore(stats) {
    if (stats?.totalProductsInDb === 0) {
      return 0;
    }

    if (stats?.flaggedProductsMissingFromDb === 0) {
      return 100;
    }

    const missingRatio = stats?.flaggedProductsMissingFromDb / stats?.totalFlaggedProducts;
    return Math.max(0, Math.round((1 - missingRatio) * 100));
  }
}

export const productSyncService = new ProductSyncService();
